import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { KeyRound, Shield, RefreshCw, Copy, Trash2, UserRound, Globe, CheckCircle2, AlertTriangle, Lock, Eye, EyeOff, X, LogOut } from 'lucide-react';
import { useGoogleLogin } from '@react-oauth/google';
import { STORAGE_KEY, ENGINE_URL_KEY } from './portalSessionKeys';
import { consumePostLoginRedirect } from './authSession';

export { STORAGE_KEY, ENGINE_URL_KEY };
const PROD_ENGINE_FALLBACK = 'https://engine-bc2n.onrender.com';
const DEFAULT_ENGINE_URL = (process.env.REACT_APP_ENGINE_URL || '').trim() || (process.env.NODE_ENV === 'production' ? PROD_ENGINE_FALLBACK : '');
const BUILD_SPECIFIED_ENGINE = !!(process.env.REACT_APP_ENGINE_URL || '').trim();

function safeJsonParse(raw) {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function formatDate(raw) {
  if (!raw) return '-';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleString();
}

async function apiFetch(baseUrl, path, token, options = {}) {
  const cleanBase = (baseUrl || '').trim().replace(/\/$/, '');
  const url = cleanBase ? `${cleanBase}${path}` : path;

  const authType = options.authType || 'api_key';
  let authHeader = {};
  if (authType === 'jwt') {
    authHeader = { Authorization: `Bearer ${token}` };
  } else if (authType === 'api_key') {
    authHeader = { 'X-API-KEY': token };
  }

  const headers = {
    'Content-Type': 'application/json',
    ...authHeader,
    ...(options.headers || {}),
  };
  const res = await fetch(url, {
    ...options,
    headers,
  });
  return res;
}

const ApiKeyPortal = ({ authOnly = false, initialSession = null, onAuthenticated, onSessionChange, onNavigate, googleLoginEnabled = false }) => {
  const savedSession = useMemo(() => {
    if (typeof window === 'undefined') return null;
    return safeJsonParse(window.localStorage.getItem(STORAGE_KEY));
  }, []);
  const savedEngineUrl = useMemo(() => {
    if (typeof window === 'undefined') return '';
    return (window.localStorage.getItem(ENGINE_URL_KEY) || '').trim();
  }, []);

  const sessionSeed = initialSession || savedSession;

  const [engineUrl, setEngineUrl] = useState(() => {
    if (BUILD_SPECIFIED_ENGINE) return DEFAULT_ENGINE_URL;
    return savedEngineUrl || sessionSeed?.engineUrl || DEFAULT_ENGINE_URL;
  });
  const [authMode, setAuthMode] = useState(() => {
    if (authOnly) return 'password';
    return sessionSeed?.authMode || 'api_key';
  });
  const [token, setToken] = useState(() => sessionSeed?.token || '');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [registerConfirmPassword, setRegisterConfirmPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [pendingVerifyEmail, setPendingVerifyEmail] = useState('');
  const [notice, setNotice] = useState('');
  const [busyAction, setBusyAction] = useState('');
  const [me, setMe] = useState(() => sessionSeed?.me || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [showPasswordRegister, setShowPasswordRegister] = useState(false);
  const [showPasswordLogin, setShowPasswordLogin] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordStep, setForgotPasswordStep] = useState('email'); // 'email' | 'code' | 'new-password'
  const [forgotPasswordCode, setForgotPasswordCode] = useState('');
  const [forgotPasswordNewPassword, setForgotPasswordNewPassword] = useState('');
  const [forgotPasswordConfirmPassword, setForgotPasswordConfirmPassword] = useState('');

  const [ownerId, setOwnerId] = useState(() => sessionSeed?.ownerId || '');
  const [keys, setKeys] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  const [name, setName] = useState('');
  const [role, setRole] = useState('user');
  const [scopes, setScopes] = useState('read');
  const [expiresAt, setExpiresAt] = useState('');
  const [lastCreated, setLastCreated] = useState(null);
  const hydratedRef = useRef(false);

  useEffect(() => {
    hydratedRef.current = true;
  }, [authOnly]);

  useEffect(() => {
    if (!initialSession || me) return;
    setToken(initialSession.token || '');
    setMe(initialSession.me || null);
    setOwnerId(initialSession.ownerId || '');
    setAuthMode(initialSession.authMode || (authOnly ? 'password' : 'api_key'));
    if (initialSession.engineUrl) {
      setEngineUrl(initialSession.engineUrl);
    }
  }, [initialSession, me, authOnly]);

  useEffect(() => {
    if (authOnly || me || !token.trim()) {
      return;
    }

    let cancelled = false;

    const hydrateSession = async () => {
      try {
        const res = await apiFetch(engineUrl, '/v1/account/me', token, {
          method: 'GET',
          authType: authMode === 'password' ? 'jwt' : 'api_key',
        });

        if (!res.ok) {
          return;
        }

        const body = await res.json();
        if (cancelled) return;

        setMe(body);
        if (body.role !== 'admin') {
          setOwnerId(body.owner_id || '');
        }
        if (typeof onSessionChange === 'function') {
          onSessionChange({
            engineUrl,
            authMode: authMode || 'jwt',
            token,
            me: body,
            ownerId: body.owner_id || '',
          });
        }
      } catch {
        // Keep the portal usable; if hydration fails, the provider can still refresh or re-open.
      }
    };

    hydrateSession();
    return () => {
      cancelled = true;
    };
  }, [authOnly, me, token, engineUrl, authMode, onSessionChange]);

  useEffect(() => {
    if (!hydratedRef.current) {
      return;
    }

    if (me) {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          engineUrl,
          authMode,
          token,
          me,
          ownerId,
        })
      );
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }

    // Always persist the selected engine URL independently so new users don't need to re-enter it
    try {
      if (engineUrl && !BUILD_SPECIFIED_ENGINE) {
        localStorage.setItem(ENGINE_URL_KEY, engineUrl);
      }
    } catch (e) {
      // ignore storage errors
    }
  }, [engineUrl, authMode, token, me, ownerId]);

  const isAdmin = me?.role === 'admin';
  const effectiveOwner = isAdmin ? ownerId : me?.owner_id || '';
  const effectiveRole = isAdmin ? role : 'user';

  const canCreate = useMemo(() => {
    if (!me) return false;
    if (!name.trim()) return false;
    if (!effectiveOwner.trim()) return false;
    if (!isAdmin && effectiveRole !== 'user') return false;
    return true;
  }, [me, name, effectiveOwner, isAdmin, effectiveRole]);

  const connect = async () => {
    setNotice('');
    setError('');
    setBusyAction('login');
    setLoading(true);
    setLastCreated(null);
    try {
      let sessionToken = token;
      let authType = authMode;

      if (authMode === 'password') {
        if (!email.trim() || !password.trim()) {
          setError('Email and password are required for login.');
          setBusyAction('');
          setLoading(false);
          return;
        }
        const loginRes = await apiFetch(engineUrl, '/v1/account/login', 'unused', {
          method: 'POST',
          authType: 'none',
          headers: {},
          body: JSON.stringify({ email: email.trim(), password }),
        });
        if (!loginRes.ok) {
          const message = await loginRes.text();
          setError(`Login failed (${loginRes.status}): ${message || 'Check credentials.'}`);
          setBusyAction('');
          setLoading(false);
          return;
        }
        const loginBody = await loginRes.json();
        sessionToken = loginBody?.token || '';
        authType = 'jwt';
        if (!sessionToken) {
          setError('Login response did not include a token.');
          setBusyAction('');
          setLoading(false);
          return;
        }
        setToken(sessionToken);
      } else if (!sessionToken.trim()) {
        setError('API key is required.');
        setBusyAction('');
        setLoading(false);
        return;
      }

      const res = await apiFetch(engineUrl, '/v1/account/me', sessionToken, { method: 'GET', authType });
      if (!res.ok) {
        setError(`Login failed (${res.status}). Check engine URL and credentials.`);
        setBusyAction('');
        setLoading(false);
        return;
      }
      const body = await res.json();
      setMe(body);
      if (body.role !== 'admin') {
        setOwnerId(body.owner_id || '');
      }
      const fullSession = {
        engineUrl,
        authMode: 'jwt',
        token: sessionToken,
        me: body,
        ownerId: body.owner_id || '',
      };
      if (typeof onSessionChange === 'function') {
        onSessionChange(fullSession);
      }
      if (typeof onAuthenticated === 'function') {
        onAuthenticated(fullSession);
      }
      setBusyAction('');
      setLoading(false);
    } catch (err) {
      setError('Unable to reach engine. Confirm URL and CORS/network settings.');
      setBusyAction('');
      setLoading(false);
    }
  };

  const disconnect = () => {
    setMe(null);
    setToken('');
    setKeys([]);
    setOwnerId('');
    setLastCreated(null);
    setError('');
    setNotice('');
    setPassword('');
    localStorage.removeItem(STORAGE_KEY);
    if (typeof window !== 'undefined') {
      window.sessionStorage.clear();
    }
    if (typeof onSessionChange === 'function') {
      onSessionChange(null);
    }
  };

  const register = async () => {
    setNotice('');
    setError('');
    setBusyAction('register');
    if (!registerEmail.trim() || !registerPassword.trim()) {
      setError('Registration email and password are required.');
      setBusyAction('');
      return;
    }
    if (registerPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      setBusyAction('');
      return;
    }
    if (registerPassword !== registerConfirmPassword) {
      setError('Passwords do not match.');
      setBusyAction('');
      return;
    }

    setLoading(true);
    try {
      const res = await apiFetch(engineUrl, '/v1/account/register', 'unused', {
        method: 'POST',
        authType: 'none',
        headers: {},
        body: JSON.stringify({
          email: registerEmail.trim(),
          password: registerPassword,
        }),
      });
      if (!res.ok) {
        const message = await res.text();
        if (message.toLowerCase().includes('smtp credentials not configured')) {
          setError('Registration is blocked: backend email SMTP is not configured yet. Set SMTP_* env vars on Render.');
        } else {
          setError(`Registration failed (${res.status}): ${message}`);
        }
        setLoading(false);
        return;
      }

      setEmail(registerEmail.trim());
      setPassword(registerPassword);
      setAuthMode('password');
      setPendingVerifyEmail(registerEmail.trim());
      setRegisterEmail('');
      setRegisterPassword('');
      setRegisterConfirmPassword('');
      setNotice('Signup successful. Verification code sent to email. Enter code to verify.');
    } catch {
      setError('Registration request failed.');
    } finally {
      setBusyAction('');
      setLoading(false);
    }
  };

  const verifyEmail = async () => {
    setNotice('');
    setError('');
    const targetEmail = (pendingVerifyEmail || registerEmail || email).trim();
    if (!targetEmail || !verificationCode.trim()) {
      setError('Email and verification code are required.');
      return;
    }

    setBusyAction('verify');
    setLoading(true);
    try {
      const res = await apiFetch(engineUrl, '/v1/account/verify-email', 'unused', {
        method: 'POST',
        authType: 'none',
        headers: {},
        body: JSON.stringify({ email: targetEmail, code: verificationCode.trim() }),
      });
      if (!res.ok) {
        const message = await res.text();
        setError(`Verification failed (${res.status}): ${message}`);
        return;
      }
      setVerificationCode('');
      setPendingVerifyEmail('');
      setEmail(targetEmail);
      setNotice('Email verified. You can now login.');
    } catch {
      setError('Verification request failed.');
    } finally {
      setBusyAction('');
      setLoading(false);
    }
  };

  const refreshKeys = useCallback(async () => {
    if (!me || !effectiveOwner) return;
    setRefreshing(true);
    setError('');
    try {
      const q = encodeURIComponent(effectiveOwner);
      const res = await apiFetch(engineUrl, `/v1/account/api-keys?owner_id=${q}`, token, {
        method: 'GET',
        authType: authMode === 'password' ? 'jwt' : 'api_key',
      });
      if (!res.ok) {
        setError(`Failed to list keys (${res.status}).`);
        setRefreshing(false);
        return;
      }
      const body = await res.json();
      setKeys(Array.isArray(body) ? body : []);
    } catch (err) {
      setError('List request failed.');
    } finally {
      setRefreshing(false);
    }
  }, [me, effectiveOwner, engineUrl, token, authMode]);

  useEffect(() => {
    if (!authOnly && me) {
      refreshKeys();
    }
  }, [authOnly, me, refreshKeys]);

  const createKey = async () => {
    if (!canCreate) return;
    setError('');
    setLoading(true);
    try {
      const payload = {
        owner_id: effectiveOwner.trim(),
        name: name.trim(),
        role: effectiveRole,
        scopes: scopes
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
      };
      if (expiresAt.trim()) {
        payload.expires_at = expiresAt.trim();
      }

      const res = await apiFetch(engineUrl, '/v1/account/api-keys', token, {
        method: 'POST',
        authType: authMode === 'password' ? 'jwt' : 'api_key',
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const message = await res.text();
        setError(`Create failed (${res.status}): ${message}`);
        setLoading(false);
        return;
      }
      const body = await res.json();
      setLastCreated(body);
      setName('');
      setScopes('read');
      setExpiresAt('');
      await refreshKeys();
    } catch (err) {
      setError('Create request failed.');
    } finally {
      setLoading(false);
    }
  };

  const revokeKey = async (id) => {
    setError('');
    try {
      const res = await apiFetch(engineUrl, `/v1/account/api-keys/${encodeURIComponent(id)}`, token, {
        method: 'DELETE',
        authType: authMode === 'password' ? 'jwt' : 'api_key',
      });
      if (!res.ok) {
        const message = await res.text();
        setError(`Revoke failed (${res.status}): ${message}`);
        return;
      }
      await refreshKeys();
    } catch (err) {
      setError('Revoke request failed.');
    }
  };

  const handleGoogleLogin = useGoogleLogin({
    onSuccess: async (codeResponse) => {
      setNotice('');
      setError('');
      setBusyAction('google-login');
      setLoading(true);
      try {
        // Exchange Google token with backend
        const googleTokenRes = await apiFetch(
          engineUrl,
          '/v1/account/google-oauth',
          'unused',
          {
            method: 'POST',
            authType: 'none',
            headers: {},
            body: JSON.stringify({ access_token: codeResponse.access_token }),
          }
        );

        if (!googleTokenRes.ok) {
          const errMsg = await googleTokenRes.text();
          setError(`Google authentication failed: ${errMsg || 'Unknown error'}`);
          setBusyAction('');
          setLoading(false);
          return;
        }

        const googleAuthBody = await googleTokenRes.json();
        const jwtToken = googleAuthBody?.token || '';
        if (!jwtToken) {
          setError('Google authentication succeeded but no token received.');
          setBusyAction('');
          setLoading(false);
          return;
        }

        // Set token and authenticate
        setToken(jwtToken);
        setAuthMode('jwt');

        // Fetch user info to complete authentication
        const meRes = await apiFetch(engineUrl, '/v1/account/me', jwtToken, {
          method: 'GET',
          authType: 'jwt',
        });

        if (!meRes.ok) {
          setError('Google authentication failed: Could not fetch user info.');
          setBusyAction('');
          setLoading(false);
          return;
        }

        const meBody = await meRes.json();
        setMe(meBody);
        if (meBody.role !== 'admin') {
          setOwnerId(meBody.owner_id || '');
        }
        const fullSession = {
          engineUrl,
          authMode: 'jwt',
          token: jwtToken,
          me: meBody,
          ownerId: meBody.owner_id || '',
        };
        if (typeof onSessionChange === 'function') {
          onSessionChange(fullSession);
        }
        
        if (typeof onAuthenticated === 'function') {
          onAuthenticated(fullSession);
        }
      } catch (err) {
        setError(`Google Sign-In error: ${err.message || 'Network error'}`);
      } finally {
        setBusyAction('');
        setLoading(false);
      }
    },
    onError: () => {
      setError('Google Sign-In cancelled or failed.');
    },
    flow: 'implicit',
  });

  const onGoogleLoginClick = () => {
    setNotice('');
    setError('');
    if (!googleLoginEnabled) {
      setError('Google Sign-In is not configured for this deployment. Set REACT_APP_GOOGLE_CLIENT_ID in Cloudflare Pages environment variables.');
      return;
    }
    handleGoogleLogin();
  };

  const requestPasswordReset = async () => {
    setError('');
    if (!forgotPasswordEmail.trim()) {
      setError('Please enter your email address.');
      return;
    }
    setBusyAction('forgot-password-request');
    setLoading(true);
    try {
      const res = await apiFetch(engineUrl, '/v1/account/request-password-reset', 'unused', {
        method: 'POST',
        authType: 'none',
        headers: {},
        body: JSON.stringify({ email: forgotPasswordEmail.trim() }),
      });
      if (!res.ok) {
        const message = await res.text();
        setError(`Request failed (${res.status}): ${message}`);
        setLoading(false);
        setBusyAction('');
        return;
      }
      setNotice('Reset code sent to your email.');
      setForgotPasswordStep('code');
    } catch {
      setError('Password reset request failed.');
    } finally {
      setBusyAction('');
      setLoading(false);
    }
  };

  const verifyResetCode = async () => {
    setError('');
    if (!forgotPasswordCode.trim()) {
      setError('Please enter the reset code.');
      return;
    }
    setBusyAction('forgot-password-verify');
    setLoading(true);
    try {
      const res = await apiFetch(engineUrl, '/v1/account/verify-reset-code', 'unused', {
        method: 'POST',
        authType: 'none',
        headers: {},
        body: JSON.stringify({ email: forgotPasswordEmail.trim(), code: forgotPasswordCode.trim() }),
      });
      if (!res.ok) {
        const message = await res.text();
        setError(`Verification failed (${res.status}): ${message}`);
        setLoading(false);
        setBusyAction('');
        return;
      }
      setNotice('Code verified. Enter your new password.');
      setForgotPasswordStep('new-password');
    } catch {
      setError('Code verification failed.');
    } finally {
      setBusyAction('');
      setLoading(false);
    }
  };

  const resetPassword = async () => {
    setError('');
    if (!forgotPasswordNewPassword.trim()) {
      setError('New password is required.');
      return;
    }
    if (forgotPasswordNewPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (forgotPasswordNewPassword !== forgotPasswordConfirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setBusyAction('forgot-password-reset');
    setLoading(true);
    try {
      const res = await apiFetch(engineUrl, '/v1/account/reset-password', 'unused', {
        method: 'POST',
        authType: 'none',
        headers: {},
        body: JSON.stringify({
          email: forgotPasswordEmail.trim(),
          code: forgotPasswordCode.trim(),
          password: forgotPasswordNewPassword,
        }),
      });
      if (!res.ok) {
        const message = await res.text();
        setError(`Reset failed (${res.status}): ${message}`);
        setLoading(false);
        setBusyAction('');
        return;
      }
      setNotice('Password reset successful! You can now log in.');
      setShowForgotPassword(false);
      setForgotPasswordStep('email');
      setForgotPasswordEmail('');
      setForgotPasswordCode('');
      setForgotPasswordNewPassword('');
      setForgotPasswordConfirmPassword('');
    } catch {
      setError('Password reset failed.');
    } finally {
      setBusyAction('');
      setLoading(false);
    }
  };

  const copyToken = async () => {
    if (!lastCreated?.token) return;
    await navigator.clipboard.writeText(lastCreated.token);
  };

  return (
    <div
      className={`relative min-h-screen text-slate-100 p-6 md:p-10 font-mono overflow-hidden ${
        authOnly
          ? 'bg-gradient-to-br from-[#0a0613] via-[#0d0a25] to-[#03101f]'
          : 'bg-slate-950'
      }`}
    >
      {authOnly && (
        <>
          <div
            aria-hidden
            className="pointer-events-none absolute -top-40 -left-40 w-[36rem] h-[36rem] rounded-full bg-violet-600/30 blur-[160px]"
          />
          <div
            aria-hidden
            className="pointer-events-none absolute top-1/3 -right-32 w-[34rem] h-[34rem] rounded-full bg-cyan-500/25 blur-[140px]"
          />
          <div
            aria-hidden
            className="pointer-events-none absolute bottom-0 left-1/4 w-[28rem] h-[28rem] rounded-full bg-fuchsia-500/20 blur-[140px]"
          />
        </>
      )}
      <div className="relative max-w-6xl mx-auto space-y-6">
        <header
          className={`rounded-3xl border p-8 md:p-10 shadow-2xl backdrop-blur ${
            authOnly
              ? 'border-violet-500/20 bg-white/[0.04] ring-1 ring-white/5'
              : 'border-slate-700 bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800'
          }`}
        >
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <p className="text-[11px] uppercase tracking-[0.4em] text-cyan-300/80 font-semibold inline-flex items-center gap-2">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-cyan-300 shadow-[0_0_8px_2px_rgba(34,211,238,0.7)]" />
                {authOnly ? 'AI Execution Gateway · Vault' : 'Phase 2'}
              </p>
              <h1
                className={`mt-2 font-black tracking-tight ${
                  authOnly ? 'text-4xl md:text-5xl' : 'text-3xl md:text-4xl'
                } bg-gradient-to-r ${
                  authOnly
                    ? 'from-violet-200 via-fuchsia-200 to-cyan-200'
                    : 'from-blue-400 to-cyan-400'
                } bg-clip-text text-transparent`}
              >
                {authOnly ? 'Sign in to your trading vault' : 'API Key Access Portal'}
              </h1>
              <p className="text-slate-300/90 text-sm md:text-base mt-3 max-w-xl">
                {authOnly
                  ? 'A non-custodial workspace for wallets, exchanges and autonomous agents. Sign in once, route execution everywhere.'
                  : 'Self-service key operations with owner scoping and one-time token reveal.'}
              </p>
              {authOnly && (
                <div className="mt-5 flex flex-wrap gap-2 text-[10px] uppercase tracking-[0.2em] font-semibold">
                  <span className="rounded-full border border-violet-400/30 bg-violet-500/10 text-violet-200 px-3 py-1">
                    Wallets
                  </span>
                  <span className="rounded-full border border-cyan-400/30 bg-cyan-500/10 text-cyan-200 px-3 py-1">
                    Exchanges
                  </span>
                  <span className="rounded-full border border-fuchsia-400/30 bg-fuchsia-500/10 text-fuchsia-200 px-3 py-1">
                    MCP routing
                  </span>
                  <span className="rounded-full border border-emerald-400/30 bg-emerald-500/10 text-emerald-200 px-3 py-1">
                    Autonomous agents
                  </span>
                </div>
              )}
            </div>
            {!authOnly && (
              <button
                type="button"
                onClick={() => {
                  if (typeof onNavigate === 'function') {
                    onNavigate('/');
                  } else {
                    window.location.assign('/');
                  }
                }}
                className="text-xs border border-slate-600 rounded px-3 py-1.5 text-slate-300 hover:text-white hover:border-slate-500 transition-colors"
              >
                Back to Engine UI
              </button>
            )}
          </div>
        </header>

        <section
          className={`rounded-3xl border p-6 md:p-7 shadow-xl backdrop-blur ${
            authOnly
              ? 'border-white/10 bg-white/[0.03] ring-1 ring-white/5'
              : 'border-slate-800 bg-slate-900/70'
          }`}
        >
          {authOnly && !me && (
            <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3 rounded-2xl border border-white/5 bg-gradient-to-r from-slate-900/80 via-slate-900/70 to-slate-900/80 p-4">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Connect to backend</p>
                <p className="text-sm text-slate-100 font-medium mt-1">
                  Your engine, your keys. No data leaves your workspace until you sign in.
                </p>
              </div>
              <div className="flex flex-wrap gap-2 text-[10px] uppercase tracking-[0.2em] font-semibold">
                <span className="rounded-full border border-emerald-400/30 bg-emerald-500/10 text-emerald-200 px-3 py-1">Encrypted</span>
                <span className="rounded-full border border-slate-600/40 bg-slate-700/30 text-slate-200 px-3 py-1">Self-hosted</span>
              </div>
            </div>
          )}
          <div className={authOnly ? 'grid grid-cols-1 lg:grid-cols-2 gap-5' : 'grid grid-cols-1 lg:grid-cols-5 gap-3'}>
            <div className={authOnly ? '' : 'lg:col-span-2'}>
              <label className="text-xs uppercase text-slate-400">Engine URL</label>
              <div className="relative mt-1">
                <Globe size={14} className="absolute left-3 top-3 text-slate-500" />
                <input
                  value={engineUrl}
                  onChange={(e) => setEngineUrl(e.target.value)}
                  placeholder={BUILD_SPECIFIED_ENGINE ? 'Using configured backend' : 'Leave empty for same-origin'}
                  disabled={BUILD_SPECIFIED_ENGINE}
                  className="w-full bg-slate-950 border border-slate-700 rounded-lg pl-9 pr-3 py-2 text-sm"
                />
                {BUILD_SPECIFIED_ENGINE ? (
                  <p className="mt-2 text-xs text-slate-500">Using backend from build config (REACT_APP_ENGINE_URL). Change at build/deploy settings.</p>
                ) : (
                  <p className="mt-2 text-xs text-slate-500">Engine URL is saved in your browser for future visits.</p>
                )}
              </div>
            </div>
            {!authOnly && me && (
              <div className="lg:col-span-3 flex items-end justify-between gap-3">
                <p className="text-xs text-emerald-300 border border-emerald-800 bg-emerald-950/40 rounded px-3 py-2">
                  Provider session active. You can create and manage API keys below.
                </p>
                <button
                  onClick={disconnect}
                  className="bg-slate-800 hover:bg-slate-700 rounded-lg py-2 px-3 text-sm"
                >
                  Logout
                </button>
              </div>
            )}

            {!authOnly && !me && (
              <div className="lg:col-span-3 rounded-xl border border-amber-800/60 bg-amber-950/20 p-5 text-sm text-amber-100">
                <p className="font-semibold uppercase tracking-wide text-amber-300">Provider session required</p>
                <p className="mt-2 text-amber-100/90">
                  This portal is for API/MCP providers to create and revoke keys. Log in from the dashboard first, then reopen Access Portal to manage keys without any connect step.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    if (typeof onNavigate === 'function') {
                      onNavigate('/');
                    } else {
                      window.location.assign('/');
                    }
                  }}
                  className="mt-4 inline-flex items-center gap-1 border border-amber-700 bg-amber-950/30 text-amber-200 rounded px-3 py-1.5 hover:text-white"
                >
                  Go to Dashboard Login
                </button>
              </div>
            )}

            {authOnly && !me && (
              <>
                <div className="relative rounded-2xl p-[1px] bg-gradient-to-br from-violet-500/50 via-fuchsia-500/30 to-cyan-500/40 shadow-xl">
                  <div className="rounded-2xl p-6 bg-gradient-to-br from-slate-900 via-slate-900/95 to-slate-950 space-y-4">
                  <div className="flex items-center justify-between">
                    <p className="text-[11px] uppercase tracking-[0.3em] text-violet-200 font-semibold flex items-center gap-2">
                      <UserRound size={14} /> Create account
                    </p>
                    <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500">No KYC</span>
                  </div>

                  <button
                    onClick={onGoogleLoginClick}
                    disabled={!googleLoginEnabled || loading}
                    title={!googleLoginEnabled ? 'Google OAuth client is not configured' : ''}
                    className="w-full bg-white hover:bg-slate-100 disabled:bg-slate-300 disabled:text-slate-600 disabled:cursor-not-allowed text-slate-900 rounded-xl py-2.5 px-4 text-sm font-semibold flex items-center justify-center gap-2 transition-colors shadow-md"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24">
                      <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Continue with Google
                  </button>

                  {!googleLoginEnabled && (
                    <p className="text-xs text-amber-300 -mt-1 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2">
                      Google Sign-In needs a valid web client ID baked into this build (set{' '}
                      <code className="text-amber-200/90">REACT_APP_GOOGLE_CLIENT_ID</code> in Cloudflare Pages → Variables and
                      Secrets, then <strong className="text-amber-100">redeploy</strong> the site so <code className="text-amber-200/90">npm run build</code>{' '}
                      picks it up).
                    </p>
                  )}

                  <div className="flex items-center gap-3 opacity-70">
                    <div className="flex-1 h-px bg-gradient-to-r from-transparent via-slate-600 to-transparent"></div>
                    <span className="text-[10px] tracking-[0.3em] uppercase text-slate-500">or email</span>
                    <div className="flex-1 h-px bg-gradient-to-r from-transparent via-slate-600 to-transparent"></div>
                  </div>

                  <input
                    value={registerEmail}
                    onChange={(e) => setRegisterEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  />
                  <div className="relative">
                    <input
                      type={showPasswordRegister ? 'text' : 'password'}
                      value={registerPassword}
                      onChange={(e) => setRegisterPassword(e.target.value)}
                      placeholder="Min 8 characters"
                      className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm pr-10 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswordRegister(!showPasswordRegister)}
                      className="absolute right-3 top-2.5 text-slate-500 hover:text-slate-300"
                    >
                      {showPasswordRegister ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  <div className="relative">
                    <input
                      type={showPasswordRegister ? 'text' : 'password'}
                      value={registerConfirmPassword}
                      onChange={(e) => setRegisterConfirmPassword(e.target.value)}
                      placeholder="Confirm password"
                      className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm pr-10 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswordRegister(!showPasswordRegister)}
                      className="absolute right-3 top-2.5 text-slate-500 hover:text-slate-300"
                    >
                      {showPasswordRegister ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  <button
                    onClick={register}
                    disabled={busyAction === 'register'}
                    className="w-full bg-gradient-to-r from-emerald-500 via-emerald-500 to-cyan-500 hover:opacity-90 disabled:from-slate-700 disabled:to-slate-600 rounded-xl py-2.5 text-sm font-semibold transition-all shadow-lg shadow-emerald-900/40 disabled:shadow-none"
                  >
                    {busyAction === 'register' ? 'Creating Account...' : 'Create Account'}
                  </button>

                  <div className="pt-4 border-t border-slate-700/60 space-y-3">
                    <p className="text-[11px] uppercase tracking-[0.3em] text-slate-300 font-semibold">Email verification</p>
                    <input
                      value={pendingVerifyEmail}
                      onChange={(e) => setPendingVerifyEmail(e.target.value)}
                      placeholder="Email used for signup"
                      className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    />
                    <input
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value)}
                      placeholder="6-digit code"
                      className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    />
                    <button
                      onClick={verifyEmail}
                      disabled={busyAction === 'verify'}
                      className="w-full bg-gradient-to-r from-violet-600 via-indigo-500 to-cyan-500 hover:opacity-90 disabled:from-slate-700 disabled:to-slate-600 rounded-xl py-2.5 text-sm font-semibold transition-all shadow-lg shadow-violet-900/40 disabled:shadow-none"
                    >
                      {busyAction === 'verify' ? 'Verifying...' : 'Verify Email'}
                    </button>
                  </div>
                  </div>
                </div>

                <div className="relative rounded-2xl p-[1px] bg-gradient-to-br from-cyan-400/50 via-sky-400/30 to-blue-500/40 shadow-xl">
                  <div className="rounded-2xl p-6 bg-gradient-to-br from-slate-900 via-slate-900/95 to-slate-950 space-y-4">
                  <div className="flex items-center justify-between">
                    <p className="text-[11px] uppercase tracking-[0.3em] text-cyan-200 font-semibold flex items-center gap-2">
                      <Lock size={14} /> Sign in
                    </p>
                    <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Encrypted</span>
                  </div>
                  <input
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  />
                  <div className="relative">
                    <input
                      type={showPasswordLogin ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Password"
                      className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm pr-10 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswordLogin(!showPasswordLogin)}
                      className="absolute right-3 top-2.5 text-slate-500 hover:text-slate-300"
                    >
                      {showPasswordLogin ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  <button
                    onClick={connect}
                    disabled={busyAction === 'login'}
                    className="w-full bg-gradient-to-r from-cyan-500 via-sky-500 to-blue-500 hover:opacity-90 disabled:from-slate-700 disabled:to-slate-600 rounded-xl py-2.5 text-sm font-semibold transition-all shadow-lg shadow-cyan-900/40 disabled:shadow-none"
                  >
                    {busyAction === 'login' ? 'Logging In...' : 'Log In'}
                  </button>
                  <button
                    onClick={() => {
                      setShowForgotPassword(true);
                      setForgotPasswordEmail(email);
                    }}
                    className="w-full text-center text-xs hover:text-cyan-200 transition-colors py-2 text-cyan-300"
                  >
                    Forgot your password?
                  </button>
                  </div>
                </div>
              </>
            )}
          </div>

          {!authOnly && !me && (
            <div className="lg:col-span-3 rounded-xl border border-amber-800/60 bg-amber-950/20 p-5 text-sm text-amber-100">
              <p className="font-semibold uppercase tracking-wide text-amber-300">Provider session required</p>
              <p className="mt-2 text-amber-100/90">
                This portal is for API/MCP providers to create and revoke keys. Log in from the dashboard first, then reopen Access Portal to manage keys without any connect step.
              </p>
              <button
                type="button"
                onClick={() => {
                  if (typeof onNavigate === 'function') {
                    onNavigate('/');
                  } else {
                    window.location.assign('/');
                  }
                }}
                className="mt-4 inline-flex items-center gap-1 border border-amber-700 bg-amber-950/30 text-amber-200 rounded px-3 py-1.5 hover:text-white"
              >
                Go to Dashboard Login
              </button>
            </div>
          )}

          {me && (
            <div className="mt-5 rounded-2xl border border-emerald-400/20 bg-gradient-to-r from-emerald-500/10 via-cyan-500/10 to-violet-500/10 p-4 md:p-5">
              <div className="flex flex-wrap items-center gap-3 md:gap-4">
                <span className="inline-flex items-center gap-2 rounded-full bg-emerald-500/15 border border-emerald-400/40 text-emerald-200 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider shadow-[0_0_18px_-4px_rgba(16,185,129,0.6)]">
                  <CheckCircle2 size={14} /> Authenticated
                </span>
                {me.email && (
                  <span className="inline-flex items-center gap-2 rounded-lg bg-slate-950/60 border border-slate-700/60 text-slate-200 px-3 py-1.5 text-xs">
                    <span className="text-slate-500 uppercase tracking-wider text-[10px]">Email</span>
                    <span className="font-semibold">{me.email}</span>
                  </span>
                )}
                <span className="inline-flex items-center gap-2 rounded-lg bg-slate-950/60 border border-slate-700/60 text-slate-200 px-3 py-1.5 text-xs">
                  <Shield size={12} className="text-violet-300" />
                  <span className="text-slate-500 uppercase tracking-wider text-[10px]">Role</span>
                  <span className="font-semibold uppercase">{me.role || '—'}</span>
                </span>
                <span className="inline-flex items-center gap-2 rounded-lg bg-slate-950/60 border border-slate-700/60 text-slate-200 px-3 py-1.5 text-xs">
                  <UserRound size={12} className="text-cyan-300" />
                  <span className="text-slate-500 uppercase tracking-wider text-[10px]">Owner</span>
                  <code className="text-cyan-200/90 font-mono">{(me.owner_id || '').slice(0, 8) || '—'}…</code>
                </span>
                <span className="inline-flex items-center gap-2 rounded-lg bg-slate-950/60 border border-slate-700/60 text-slate-200 px-3 py-1.5 text-xs">
                  <KeyRound size={12} className="text-emerald-300" />
                  <span className="text-slate-500 uppercase tracking-wider text-[10px]">Auth</span>
                  <span className="font-semibold uppercase">{me.auth_type || authMode}</span>
                </span>
                {authOnly && (
                  <div className="ml-auto flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        disconnect();
                        if (typeof window !== 'undefined') {
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }
                      }}
                      className="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-xs font-semibold uppercase tracking-wider border border-rose-400/50 bg-rose-500/10 text-rose-100 hover:bg-rose-500/20 shadow-sm"
                    >
                      <LogOut size={14} aria-hidden />
                      Log out
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const dest = consumePostLoginRedirect('/');
                        if (typeof onNavigate === 'function') {
                          onNavigate(dest);
                        } else {
                          window.location.assign(dest);
                        }
                      }}
                      className="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-xs font-semibold uppercase tracking-wider bg-gradient-to-r from-violet-500 via-fuchsia-500 to-cyan-500 hover:opacity-90 text-white shadow-lg shadow-violet-900/40"
                    >
                      Enter Dashboard
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {error && (
            <div className="mt-4 text-sm text-amber-200 border border-amber-800 bg-amber-950/40 rounded-lg p-3 inline-flex items-center gap-2">
              <AlertTriangle size={14} /> {error}
            </div>
          )}

          {notice && (
            <div className="mt-4 text-sm text-emerald-200 border border-emerald-800 bg-emerald-950/40 rounded-lg p-3 inline-flex items-center gap-2">
              <CheckCircle2 size={14} /> {notice}
            </div>
          )}

          {/* Forgot Password Modal */}
          {showForgotPassword && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
              <div className="bg-slate-900 border border-slate-700 rounded-xl p-6 max-w-md w-full shadow-2xl">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-slate-100">Reset Password</h2>
                  <button
                    onClick={() => {
                      setShowForgotPassword(false);
                      setForgotPasswordStep('email');
                      setForgotPasswordEmail('');
                      setForgotPasswordCode('');
                      setForgotPasswordNewPassword('');
                      setForgotPasswordConfirmPassword('');
                    }}
                    className="text-slate-400 hover:text-slate-300"
                  >
                    <X size={20} />
                  </button>
                </div>

                {forgotPasswordStep === 'email' && (
                  <div className="space-y-3">
                    <p className="text-sm text-slate-400">Enter your email address to receive a reset code.</p>
                    <input
                      value={forgotPasswordEmail}
                      onChange={(e) => setForgotPasswordEmail(e.target.value)}
                      placeholder="you@company.com"
                      className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    />
                    <button
                      onClick={requestPasswordReset}
                      disabled={busyAction === 'forgot-password-request'}
                      className="w-full bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 disabled:from-slate-700 disabled:to-slate-600 rounded-lg py-2 text-sm font-semibold transition-all"
                    >
                      {busyAction === 'forgot-password-request' ? 'Sending...' : 'Send Reset Code'}
                    </button>
                  </div>
                )}

                {forgotPasswordStep === 'code' && (
                  <div className="space-y-3">
                    <p className="text-sm text-slate-400">Enter the code sent to your email.</p>
                    <input
                      value={forgotPasswordCode}
                      onChange={(e) => setForgotPasswordCode(e.target.value)}
                      placeholder="6-digit code"
                      className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    />
                    <button
                      onClick={verifyResetCode}
                      disabled={busyAction === 'forgot-password-verify'}
                      className="w-full bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 disabled:from-slate-700 disabled:to-slate-600 rounded-lg py-2 text-sm font-semibold transition-all"
                    >
                      {busyAction === 'forgot-password-verify' ? 'Verifying...' : 'Verify Code'}
                    </button>
                  </div>
                )}

                {forgotPasswordStep === 'new-password' && (
                  <div className="space-y-3">
                    <p className="text-sm text-slate-400">Enter your new password.</p>
                    <div className="relative">
                      <input
                        type="password"
                        value={forgotPasswordNewPassword}
                        onChange={(e) => setForgotPasswordNewPassword(e.target.value)}
                        placeholder="New password (min 8 chars)"
                        className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      />
                    </div>
                    <div className="relative">
                      <input
                        type="password"
                        value={forgotPasswordConfirmPassword}
                        onChange={(e) => setForgotPasswordConfirmPassword(e.target.value)}
                        placeholder="Confirm new password"
                        className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      />
                    </div>
                    <button
                      onClick={resetPassword}
                      disabled={busyAction === 'forgot-password-reset'}
                      className="w-full bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400 disabled:from-slate-700 disabled:to-slate-600 rounded-lg py-2 text-sm font-semibold transition-all"
                    >
                      {busyAction === 'forgot-password-reset' ? 'Resetting...' : 'Reset Password'}
                    </button>
                  </div>
                )}

                {error && (
                  <div className="mt-3 text-sm text-amber-200 border border-amber-800 bg-amber-950/40 rounded-lg p-2 flex items-center gap-2">
                    <AlertTriangle size={14} /> {error}
                  </div>
                )}
              </div>
            </div>
          )}
        </section>

        {me && !authOnly && (
          <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
            <section className="xl:col-span-2 rounded-2xl border border-slate-800 bg-slate-900 p-5 space-y-4">
              <h2 className="text-sm uppercase tracking-widest text-slate-400">Create Key</h2>

              <div>
                <label className="text-xs uppercase text-slate-400">Owner ID</label>
                <input
                  value={isAdmin ? ownerId : effectiveOwner}
                  onChange={(e) => setOwnerId(e.target.value)}
                  disabled={!isAdmin}
                  className="mt-1 w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm disabled:opacity-60"
                />
              </div>

              <div>
                <label className="text-xs uppercase text-slate-400">Name</label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="my-primary-key"
                  className="mt-1 w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase text-slate-400">Role</label>
                  <select
                    value={isAdmin ? role : 'user'}
                    onChange={(e) => setRole(e.target.value)}
                    disabled={!isAdmin}
                    className="mt-1 w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm disabled:opacity-60"
                  >
                    <option value="user">user</option>
                    <option value="admin">admin</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs uppercase text-slate-400">Expires (RFC3339)</label>
                  <input
                    value={expiresAt}
                    onChange={(e) => setExpiresAt(e.target.value)}
                    placeholder="2026-12-31T23:59:59Z"
                    className="mt-1 w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs uppercase text-slate-400">Scopes (comma separated)</label>
                <input
                  value={scopes}
                  onChange={(e) => setScopes(e.target.value)}
                  placeholder="read,trade"
                  className="mt-1 w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm"
                />
              </div>

              <button
                onClick={createKey}
                disabled={!canCreate || loading}
                className="w-full bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-700 disabled:text-slate-400 rounded-lg py-2 text-sm font-semibold"
              >
                {loading ? 'Creating...' : 'Create API Key'}
              </button>

              {lastCreated?.token && (
                <div className="border border-fuchsia-800 bg-fuchsia-950/40 rounded-xl p-3">
                  <p className="text-xs uppercase tracking-wider text-fuchsia-300">One-time token reveal</p>
                  <div className="mt-2 text-sm break-all text-fuchsia-100">{lastCreated.token}</div>
                  <button onClick={copyToken} className="mt-3 inline-flex items-center gap-1 text-xs border border-fuchsia-700 rounded px-2 py-1 text-fuchsia-200 hover:text-white">
                    <Copy size={12} /> Copy token
                  </button>
                </div>
              )}
            </section>

            <section className="xl:col-span-3 rounded-2xl border border-slate-800 bg-slate-900 p-5">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <h2 className="text-sm uppercase tracking-widest text-slate-400">Key Inventory</h2>
                <div className="flex items-center gap-2">
                  {isAdmin && (
                    <input
                      value={ownerId}
                      onChange={(e) => setOwnerId(e.target.value)}
                      placeholder="owner-id"
                      className="bg-slate-950 border border-slate-700 rounded-lg px-3 py-1.5 text-sm"
                    />
                  )}
                  <button
                    onClick={refreshKeys}
                    disabled={refreshing || !effectiveOwner}
                    className="inline-flex items-center gap-1 border border-slate-700 rounded-lg px-3 py-1.5 text-sm hover:border-slate-500 disabled:opacity-60"
                  >
                    <RefreshCw size={13} className={refreshing ? 'animate-spin' : ''} /> Refresh
                  </button>
                </div>
              </div>

              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-slate-400 border-b border-slate-800">
                      <th className="py-2 pr-3">Name</th>
                      <th className="py-2 pr-3">Role</th>
                      <th className="py-2 pr-3">Created</th>
                      <th className="py-2 pr-3">Last Used</th>
                      <th className="py-2 pr-3">Status</th>
                      <th className="py-2">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {keys.length === 0 && (
                      <tr>
                        <td colSpan={6} className="py-6 text-center text-slate-500">No keys found for this owner.</td>
                      </tr>
                    )}
                    {keys.map((k) => (
                      <tr key={k.id} className="border-b border-slate-800/80">
                        <td className="py-3 pr-3">
                          <div className="font-semibold text-slate-200">{k.name || '-'}</div>
                          <div className="text-xs text-slate-500 break-all">{k.id}</div>
                        </td>
                        <td className="py-3 pr-3">{k.role || '-'}</td>
                        <td className="py-3 pr-3 text-slate-300">{formatDate(k.created_at)}</td>
                        <td className="py-3 pr-3 text-slate-300">{formatDate(k.last_used)}</td>
                        <td className="py-3 pr-3">
                          {k.revoked ? (
                            <span className="text-xs border border-rose-800 text-rose-300 bg-rose-950/40 rounded px-2 py-0.5">revoked</span>
                          ) : (
                            <span className="text-xs border border-emerald-800 text-emerald-300 bg-emerald-950/40 rounded px-2 py-0.5">active</span>
                          )}
                        </td>
                        <td className="py-3">
                          {!k.revoked && (
                            <button
                              onClick={() => revokeKey(k.id)}
                              className="inline-flex items-center gap-1 text-xs border border-rose-800 text-rose-300 hover:text-rose-100 rounded px-2 py-1"
                            >
                              <Trash2 size={12} /> Revoke
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApiKeyPortal;
